import { Navigate, Route, Routes, useLocation } from "react-router";
import type { ReactElement } from "react";
import { Layout } from "./components/Layout.tsx";
import { SessionProvider, useSession } from "./session.tsx";
import { Home } from "./pages/Home.tsx";
import { HowItWorks } from "./pages/HowItWorks.tsx";
import { Login } from "./pages/Login.tsx";
import { Signup } from "./pages/Signup.tsx";
import { Drill } from "./pages/Drill.tsx";
import { Scoreboard } from "./pages/Scoreboard.tsx";
import { Profile } from "./pages/Profile.tsx";
import { Problems } from "./pages/Problems.tsx";
import { Support } from "./pages/Support.tsx";
import { Privacy, Terms } from "./pages/Legal.tsx";

/**
 * Gate for routes that need an account.
 *
 * Waits for the session to resolve before deciding. The session lives in an
 * httpOnly cookie that page JavaScript cannot read, so "am I signed in" is a
 * round trip, and redirecting during it would throw a signed-in user out of
 * their own app on every refresh.
 */
function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useSession();
  const location = useLocation();
  if (loading) return <div className="centred muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

function NotFound() {
  return (
    <div className="prose-page">
      <h1>Not here</h1>
      <p className="muted">That page does not exist.</p>
    </div>
  );
}

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="drills" element={<Problems />} />
          <Route path="scoreboard" element={<Scoreboard />} />
          <Route path="support" element={<Support />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          {/* Deliberately public since 21 Aug 2026. Asking a stranger to open an
              account before they can try one problem is the highest-friction
              possible first step for a free tool nobody has heard of, and the
              bank, the runner and the scheduler are all client-side, so the
              wall was buying nothing. A guest gets the drill and no schedule,
              which is the honest split: the account holds the schedule. */}
          <Route path="drill" element={<Drill />} />
          {/* The same screen with a drill named. The scheduler is still the
              default route; this one exists so a problem can be linked to, from
              the list or from anywhere else. */}
          <Route path="drill/:drillId" element={<Drill />} />
          <Route
            path="profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}
